import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI } from '../../../app/api';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function TransactionReport() {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        status: 'ALL'
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [summary, setSummary] = useState({
        totalAmount: '0.00',
        transactionCount: 0
    });
    const [searchTrigger, setSearchTrigger] = useState(0);

    useEffect(() => {
        fetchTransactions();
    }, [pagination.page, searchTrigger]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const response = await postAPI('/dashboard/transactions', {
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status,
                page: pagination.page,
                limit: pagination.limit
            });

            if (response?.status === 1) {
                setTransactions(response.data.transactions || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination.total,
                    totalPages: response.data.pagination.totalPages
                }));
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        setSearchTrigger(prev => prev + 1);
    };

    const handleReset = () => {
        setFilters({ startDate: '', endDate: '', status: 'ALL' });
        setPagination(prev => ({ ...prev, page: 1 }));
        setSearchTrigger(prev => prev + 1);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `₹ ${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'PAID': 'badge bg-success',
            'PENDING': 'badge bg-warning text-dark',
            'FAILED': 'badge bg-danger',
            'REFUNDED': 'badge bg-secondary'
        };
        return <span className={statusClasses[status] || 'badge bg-secondary'}>{status}</span>;
    };

    // Export to Excel
    const exportToExcel = () => {
        const exportData = transactions.map(txn => ({
            'Date': formatDate(txn.date),
            'Invoice No.': txn.invoice_number || 'N/A',
            'Payee Name': txn.payment_name || 'N/A',
            'Amount': parseFloat(txn.amount).toFixed(2),
            'Method': txn.method,
            'Category': txn.category,
            'Status': txn.status
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

        // Generate filename with date
        const fileName = `Transaction_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    // Export to PDF
    const exportToPDF = () => {
        try {
            const doc = new jsPDF();

            // Title
            doc.setFontSize(18);
            doc.text('Transaction Report', 14, 22);

            // Summary - use Rs. instead of ₹ for better font compatibility
            doc.setFontSize(11);
            doc.text('Total Amount: Rs. ' + parseFloat(summary.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 }), 14, 32);
            doc.text('Total Transactions: ' + summary.transactionCount, 14, 38);
            doc.text('Generated: ' + new Date().toLocaleString('en-IN'), 14, 44);

            // Table data - use Rs. instead of ₹
            const tableData = transactions.map(txn => [
                formatDate(txn.date),
                txn.invoice_number || 'N/A',
                txn.payment_name || 'N/A',
                'Rs. ' + parseFloat(txn.amount).toFixed(2),
                txn.method || 'N/A',
                txn.category || 'N/A',
                txn.status || 'N/A'
            ]);

            autoTable(doc, {
                startY: 50,
                head: [['Date', 'Invoice No.', 'Payee Name', 'Amount', 'Method', 'Category', 'Status']],
                body: tableData,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185] }
            });

            // Generate filename with date
            const fileName = 'Transaction_Report_' + new Date().toISOString().split('T')[0] + '.pdf';
            doc.save(fileName);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    return (
        <Wrapper is_loader={loading}>
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">Transaction Report</h3>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-success"
                                onClick={exportToExcel}
                                disabled={transactions.length === 0}
                            >
                                <i className="mdi mdi-file-excel me-1"></i> Excel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={exportToPDF}
                                disabled={transactions.length === 0}
                            >
                                <i className="mdi mdi-file-pdf-box me-1"></i> PDF
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
                                ← Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="card bg-gradient-primary text-white">
                        <div className="card-body">
                            <h5 className="card-title">Total Amount</h5>
                            <h2>{formatCurrency(summary.totalAmount)}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card bg-gradient-info text-white">
                        <div className="card-body">
                            <h5 className="card-title">Total Transactions</h5>
                            <h2>{summary.transactionCount}</h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row align-items-end">
                        <div className="col-md-4">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-4">
                            <button className="btn btn-primary me-2" onClick={handleSearch}>
                                Search
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleReset}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th>Date</th>
                                    <th>Invoice No.</th>
                                    <th>Payee Name</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.length > 0 ? (
                                    transactions.map((txn, index) => (
                                        <tr key={txn.id || index}>
                                            <td>{formatDate(txn.date)}</td>
                                            <td>{txn.invoice_number || 'N/A'}</td>
                                            <td>{txn.payment_name || 'N/A'}</td>
                                            <td>{formatCurrency(txn.amount)}</td>
                                            <td>{txn.method}</td>
                                            <td>{txn.category}</td>
                                            <td>{getStatusBadge(txn.status)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            {loading ? 'Loading...' : 'No transactions found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <span>
                                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                            </span>
                            <nav>
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                            disabled={pagination.page === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        const pageNum = Math.max(1, pagination.page - 2) + i;
                                        if (pageNum > pagination.totalPages) return null;
                                        return (
                                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                            disabled={pagination.page === pagination.totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>
        </Wrapper>
    );
}
